<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import CircleFill from '@primeicons/vue/circle-fill';
import {
  GpxMap,
  GpxMapBasemapToggle,
  GpxMapFitBoundsControl,
  GpxMapZoomControl,
  useMapInteractions,
} from '@gpx-spa/ui';
import ProgressSpinner from 'primevue/progressspinner';
import { createLeafletMapProvider } from '@gpx-spa/geo-adapters';
import {
  latLngBoundsFromLngLatCoordinates,
  operationalMapLayerId,
  unionManyLatLngBounds,
} from '@gpx-spa/geo-domain';
import type {
  GeoJsonFeatureCollection,
  LatLngBounds,
  MapHitEvent,
  MapMarker,
  MapCloudPoint,
  MapPointCloudSetOptions,
  MapProvider,
} from '@gpx-spa/geo-domain';
import type { V2CurrentPositionRow } from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import type { ShipmentsTrackBatchRow } from '@gpx-spa/queries/src/getAccountShipmentsTracksBatch';
import {
  APP_MAP_SATELLITE_TILE_URL,
  APP_MAP_TILE_OPTIONS,
  APP_MAP_TILE_URL,
  getAppMapTileAttribution,
  isMapAttributionVisible,
} from '@/utils/app-map-tiles';
import { COMPOSER_MAP_DEFAULT_CENTER } from '@/utils/shipment-map-constants';
import { decodeShipmentTrackRow } from '@/utils/shipment-detail-map-decode';
import {
  CONVOY_TRACK_GRAPH_OPTIONS_VERSION,
  convoyMarkerSurface,
} from '@/map/operational/convoy';
import {
  buildShipmentTrackDisplay,
  mergeShipmentTrackPointClouds,
} from '@/map/operational/track-display/build-shipment-track-display';
import {
  buildRawDeviceTracksDisplay,
  mergeRawDeviceTrackLineCollections,
} from '@/map/operational/track-display/build-raw-device-tracks-display';
import { mergeTrackPointClouds } from '@/map/operational/build-track-point-cloud';
import { trackVertexSurface } from '@/map/operational/track-vertex-surface';
import { useConvoyLiveMap } from '@/composables/useConvoyLiveMap';
import { openExceptionsForDevice } from '@/utils/shipment-exception-device-join';
import { displayExceptionEventTitle } from '@/utils/shipment-exception-display';
import { namedTrackersFromShipment } from '@/utils/named-tracker-set';
import { CORRIDOR_BUFFER_ALGORITHM_VERSION } from '@/map/operational/corridor-buffer-cache-constants';
import { createCorridorBufferIndexedDbAdapter } from '@/map/operational/corridor-buffer-idb-adapter';
import { fingerprintPathAndRouteLineLayers } from '@/map/operational/operational-map-layer-fingerprints';
import {
  buildShipmentRouteCorridorFeatureCollection,
  latLngBoundsFromPolygonFeatureCollection,
} from '@/map/operational/shipment-route-corridor-geojson';
import {
  computeShipmentCorridorInputHash,
  shipmentHasCorridorHashableRoutes,
} from '@/map/operational/shipment-corridor-input-hash';
import { useCorridorBufferCacheStore } from '@/stores/useCorridorBufferCacheStore';
import {
  OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY,
  useOperationalMapLayersStore,
} from '@/stores/useOperationalMapLayersStore';
import { shipmentRouteSegmentsToFeatureCollection } from '@/map/operational/shipment-route-segments-geojson';
import {
  latLngBoundsFromStopsGeojson,
  v2ShipmentStopsToBoundariesFeatureCollection,
  v2ShipmentStopsToNumberedMarkers,
} from '@/map/operational/v2-shipment-stops-map';
import {
  exceptionMarkerSurface,
  shipmentExceptionsToMapMarkers,
} from '@/map/operational/exceptions';
import { useOperationalMapLayerVisibility } from '@/composables/useOperationalMapLayerVisibility';
import type { OperationalZoomVisibilityRule } from '@/composables/useOperationalMapLayerVisibility';
import { useOperationalMapData } from '@/composables/useOperationalMapData';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_OVERLAY,
} from '@/constants/progress-spinner';
import { afterNextBrowserPaint } from '@/utils/after-next-browser-paint';
import { useMapBasemapModeStore } from '@/stores/useMapBasemapModeStore';
import { usePersistedMapBasemap } from '@/composables/usePersistedMapBasemap';
import ShipmentOperationalMapLayersMenu from './ShipmentOperationalMapLayersMenu.vue';
import ConvoyGroupHoverOverlay from './ConvoyGroupHoverOverlay.vue';

const POSITION_LAYER_ENTITY_ID = 'combined';

const OPERATIONAL_MAP_LAYER_OPTIONS = [
  { id: 'track', label: 'Track' },
  { id: 'rawDeviceTracks', label: 'Raw device tracks' },
  { id: 'routeLines', label: 'Route lines' },
  { id: 'corridor', label: 'Corridors' },
  { id: 'currentPosition', label: 'Current device positions' },
  { id: 'stopGeojson', label: 'Stop areas' },
  { id: 'stopMarkers', label: 'Stop markers' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'closedExceptions', label: 'Show closed exceptions' },
] as const;

const props = withDefaults(
  defineProps<{
    accountId: number;
    shipments: V2ShipmentDetail[];
    zoomVisibilityRules?: OperationalZoomVisibilityRule[];
    /** 100 = full geometry; lower simplifies route lines and corridor buffers (list view). */
    qualityPercent?: number;
    /**
     * When false, track/route/positions batch APIs stay off and the map shows a loading overlay.
     * Use while the parent list query is refetching (e.g. map page size) so batch keys match props.
     */
    operationalBatchEnabled?: boolean;
    /**
     * Suffix for persisted layer toggles in IndexedDB (`operationalMapLayers:<key>`).
     * Use different values for list vs detail map so visibility prefs are independent.
     */
    layersPersistenceKey?: string;
    /**
     * Exception rows for map markers (detail map). Open/closed filtering is
     * applied in `shipmentExceptionsToMapMarkers` via the Layers toggle.
     */
    exceptions?: ShipmentExceptionRow[];
  }>(),
  {
    zoomVisibilityRules: () => [],
    shipments: () => [],
    qualityPercent: 100,
    operationalBatchEnabled: true,
    layersPersistenceKey: OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY,
    exceptions: () => [],
  },
);

const normalizedRouteQualityPercent = computed(() => {
  const q = Math.round(props.qualityPercent ?? 100);
  if (!Number.isFinite(q)) return 100;
  return Math.min(100, Math.max(1, q));
});

const operationalMapLayersStore = useOperationalMapLayersStore();

const tracksLayerFetchEnabled = computed(
  () =>
    operationalMapLayersStore.layerVisibility.track ||
    operationalMapLayersStore.layerVisibility.rawDeviceTracks,
);
const routesLayerFetchEnabled = computed(
  () =>
    operationalMapLayersStore.layerVisibility.routeLines ||
    operationalMapLayersStore.layerVisibility.corridor,
);
const positionsLayerFetchEnabled = computed(
  () => operationalMapLayersStore.layerVisibility.currentPosition,
);

const { tracksQuery, routesQuery, positionsQuery, batchShipmentIds } =
  useOperationalMapData(
    () => props.accountId,
    () => props.shipments,
    {
      operationalBatchEnabled: () => props.operationalBatchEnabled,
      tracksFetchEnabled: tracksLayerFetchEnabled,
      routesFetchEnabled: routesLayerFetchEnabled,
      positionsFetchEnabled: positionsLayerFetchEnabled,
    },
  );

const mapReady = ref(false);
/** False until the shell has painted; avoids blocking UI right after map init with GeoJSON work. */
const operationalLayersPaintEnabled = ref(false);

const corridorBufferCache = useCorridorBufferCacheStore();
// Register IDB adapter synchronously in setup (not onMounted): @ready from the
// child map often fires before parent onMounted, so the first corridor build
// would skip IndexedDB reads and redo worker/Turf work on every full reload.
corridorBufferCache.setPersistAdapter(createCorridorBufferIndexedDbAdapter());

let offMapMoveEnd: (() => void) | undefined;

watch(
  () => props.layersPersistenceKey,
  (key) => {
    void operationalMapLayersStore.activatePersistenceScope(
      key ?? OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY,
    );
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  offMapMoveEnd?.();
  offMapMoveEnd = undefined;
  corridorBufferCache.setPersistAdapter(null);
});

const lastCorridorBoundsBySid = ref(new Map<number, LatLngBounds>());
const pathRouteFingerprintBySid = ref<Record<number, string>>({});
const corridorInputHashBySid = ref<Record<number, string>>({});
/** When set matches current batch shipment ids, combined corridor GeoJSON can be skipped. */
const lastCorridorCombinedPaintKey = ref<string>('');
/** Same for combined path + route layers. */
const lastPathRouteCombinedPaintKey = ref<string>('');
/** Skip position marker setMarkers when payload matches last paint (e.g. TanStack refetch). */
const lastPositionsPaintFingerprint = ref<string>('');

const lastPaintedShipmentIds = ref<number[]>([]);

const userAdjustedMapView = ref(false);
const treatNextMoveEndAsProgrammatic = ref(false);
const mapMoveIntentTrackingEnabled = ref(false);

watch(
  () => batchShipmentIds.value.join(','),
  () => {
    pathRouteFingerprintBySid.value = {};
    corridorInputHashBySid.value = {};
    lastCorridorCombinedPaintKey.value = '';
    lastPathRouteCombinedPaintKey.value = '';
    lastPositionsPaintFingerprint.value = '';
    lastCorridorBoundsBySid.value.clear();
    userAdjustedMapView.value = false;
    mapMoveIntentTrackingEnabled.value = false;
  },
);

const mapBasemapStore = useMapBasemapModeStore();

const mapProvider: MapProvider = createLeafletMapProvider({
  basemap: {
    streets: { rasterUrl: APP_MAP_TILE_URL },
    satellite: { rasterUrl: APP_MAP_SATELLITE_TILE_URL },
    defaultMode: mapBasemapStore.basemapMode,
  },
  getTileAttribution: getAppMapTileAttribution,
  tileLayerOptions: { ...APP_MAP_TILE_OPTIONS },
  defaultCenter: COMPOSER_MAP_DEFAULT_CENTER,
  defaultZoom: 9,
  showAttribution: isMapAttributionVisible(),
  zoomControl: false,
});

const { basemapMode } = usePersistedMapBasemap(mapProvider, mapReady);

function exceptionTitlesForDevice(deviceId: number): string[] {
  return openExceptionsForDevice(props.exceptions ?? [], deviceId).map((row) =>
    displayExceptionEventTitle(row.type),
  );
}

const {
  expandedShipmentIds,
  livePaint,
  markers: convoyMarkers,
  convoyInteractionFragment,
  groupHoverTrackersFromHit,
} = useConvoyLiveMap({
  shipments: () => props.shipments,
  positions: () => positionsQuery.data.value ?? [],
  exceptionTitlesForDevice,
});

const operationalMapInteractions = computed(() => ({
  ...convoyInteractionFragment.value,
  surface(event: MapHitEvent) {
    return (
      trackVertexSurface(event)
      ?? exceptionMarkerSurface(event)
      ?? convoyMarkerSurface(event)
    );
  },
}));

const {
  lastHoverHit,
  screenPoint,
  reapply: reapplyMapInteractions,
} = useMapInteractions(mapProvider, operationalMapInteractions);

const groupHoverTrackers = computed(() =>
  groupHoverTrackersFromHit(lastHoverHit.value),
);

useOperationalMapLayerVisibility(
  () => mapProvider,
  mapReady,
  computed(() => props.zoomVisibilityRules),
);

function layerPathCombinedId(): string {
  return operationalMapLayerId('path', 'combined');
}
function layerPathVerticesCombinedId(): string {
  return operationalMapLayerId('path-vertices', 'combined');
}
function layerRawPathCombinedId(): string {
  return operationalMapLayerId('raw-path', 'combined');
}
function layerRawPathVerticesCombinedId(): string {
  return operationalMapLayerId('raw-path-vertices', 'combined');
}
function layerRouteCombinedId(): string {
  return operationalMapLayerId('route', 'combined');
}
function layerPosCombinedId(): string {
  return operationalMapLayerId('current-position', POSITION_LAYER_ENTITY_ID);
}
function layerSpiderfyCombinedId(): string {
  return operationalMapLayerId('current-position', 'spiderfy-legs');
}
function layerStopsCombinedId(): string {
  return operationalMapLayerId('stops', 'combined');
}
function layerStopGeojsonCombinedId(): string {
  return operationalMapLayerId('stop-geojson', 'combined');
}
function layerExceptionsCombinedId(): string {
  return operationalMapLayerId('exceptions', 'combined');
}
function layerCorridorCombinedId(): string {
  return operationalMapLayerId('corridor', 'combined');
}

const lastTrackVerticesPaintKey = ref('');
const fullTrackVertices = ref<MapCloudPoint[]>([]);

const TRACK_POINT_CLOUD_THINNING: MapPointCloudSetOptions = {
  thinning: { strategy: 'minGap', mode: 'auto' },
  accuracyRings: 'on-click',
};

function trackVerticesDataKey(batchIds: number[]): string {
  const parts = batchIds
    .slice()
    .sort((a, b) => a - b)
    .flatMap((sid) =>
      trackRowsForShipment(sid).map(
        (row) =>
          `${row.shipment_id}:${row.device_id}:${row.data?.encoded_polyline ?? ''}:${row.data?.timestamp ?? ''}:${row.data?.battery ?? ''}:${row.data?.fix_type ?? ''}:${row.data?.accuracy ?? ''}`,
      ),
    );
  return `${batchIds.join(',')}|${parts.join('|')}`;
}

function rebuildFullTrackVertices(batchIds: number[]): void {
  const clouds = batchIds.map((sid) => {
    const status = props.shipments.find((s) => s.id === sid)?.status;
    const decodedRows = trackRowsForShipment(sid).map((row) => ({
      device_id: row.device_id,
      decoded: decodeShipmentTrackRow(row),
    }));
    return buildShipmentTrackDisplay(decodedRows, status).pointCloud;
  });
  fullTrackVertices.value = mergeShipmentTrackPointClouds(clouds).points;
}

function paintTrackVerticesLayer(): void {
  const layer = mapProvider.addLayer(layerPathVerticesCombinedId());
  const lv = operationalMapLayersStore.layerVisibility;
  if (!lv.track || fullTrackVertices.value.length === 0) {
    layer.setPointCloud(null);
    return;
  }
  layer.setPointCloud(
    { points: fullTrackVertices.value },
    TRACK_POINT_CLOUD_THINNING,
  );
}

function paintTrackVerticesCombined(batchIds: number[]): void {
  const lv = operationalMapLayersStore.layerVisibility;
  if (!lv.track || batchIds.length === 0) {
    fullTrackVertices.value = [];
    lastTrackVerticesPaintKey.value = '';
    mapProvider.getLayer(layerPathVerticesCombinedId())?.setPointCloud(null);
    return;
  }
  const key = trackVerticesDataKey(batchIds);
  if (key === lastTrackVerticesPaintKey.value) {
    return;
  }
  lastTrackVerticesPaintKey.value = key;
  rebuildFullTrackVertices(batchIds);
  paintTrackVerticesLayer();
}

const lastRawDeviceTracksPaintKey = ref('');

function clearRawDeviceTracksLayers(): void {
  lastRawDeviceTracksPaintKey.value = '';
  mapProvider.getLayer(layerRawPathCombinedId())?.clear();
  mapProvider.getLayer(layerRawPathVerticesCombinedId())?.setPointCloud(null);
}

function paintRawDeviceTracksCombined(batchIds: number[]): void {
  const lv = operationalMapLayersStore.layerVisibility;
  if (!lv.rawDeviceTracks || batchIds.length === 0) {
    clearRawDeviceTracksLayers();
    return;
  }
  const key = trackVerticesDataKey(batchIds);
  if (key === lastRawDeviceTracksPaintKey.value) {
    return;
  }
  lastRawDeviceTracksPaintKey.value = key;

  const displays = batchIds.map((sid) => {
    const status = props.shipments.find((s) => s.id === sid)?.status;
    const decodedRows = trackRowsForShipment(sid).map((row) => ({
      device_id: row.device_id,
      decoded: decodeShipmentTrackRow(row),
    }));
    return buildRawDeviceTracksDisplay(decodedRows, status);
  });

  const lineCollection = mergeRawDeviceTrackLineCollections(
    displays.map((d) => d.lineCollection),
  );
  const pointCloud = mergeTrackPointClouds(displays.map((d) => d.pointCloud));

  mapProvider.addLayer(layerRawPathCombinedId()).setGeoJSON(lineCollection);
  const verticesLayer = mapProvider.addLayer(layerRawPathVerticesCombinedId());
  if (pointCloud.points.length === 0) {
    verticesLayer.setPointCloud(null);
  } else {
    verticesLayer.setPointCloud(pointCloud, TRACK_POINT_CLOUD_THINNING);
  }
}

/**
 * TanStack: isRefetching is true only for in-flight fetches that already have settled data (same query key).
 * First load / new key after filter change => isRefetching false while fetching => show overlay.
 */
const operationalBatchQueriesNonRefetchFetch = computed(() => {
  const needPositions =
    positionsLayerFetchEnabled.value &&
    batchShipmentIds.value.length > 0 &&
    props.shipments.some((s) => (s.device_ids?.length ?? 0) > 0);
  const tracksBlocking =
    tracksLayerFetchEnabled.value &&
    tracksQuery.isFetching.value &&
    !tracksQuery.isRefetching.value;
  const routesBlocking =
    routesLayerFetchEnabled.value &&
    routesQuery.isFetching.value &&
    !routesQuery.isRefetching.value;
  const positionsBlocking =
    needPositions &&
    positionsQuery.isFetching.value &&
    !positionsQuery.isRefetching.value;
  return tracksBlocking || routesBlocking || positionsBlocking;
});

const mapBusy = computed(() => {
  if (!props.operationalBatchEnabled) {
    return true;
  }
  return operationalBatchQueriesNonRefetchFetch.value;
});

function fingerprintOperationalPositionsPayload(
  rows: V2CurrentPositionRow[] | undefined,
): string {
  const expandKey = [...expandedShipmentIds.value]
    .sort((a, b) => a - b)
    .join(',');
  const exceptionKey = (props.exceptions ?? [])
    .map((row) => `${row.id}:${row.type}:${(row.device_ids ?? []).join('-')}`)
    .sort()
    .join(',');
  if (!rows?.length) {
    return `∅|${expandKey}|${exceptionKey}`;
  }
  return `${JSON.stringify(
    [...rows]
      .sort((a, b) => a.device_id - b.device_id)
      .map((r) => ({
        d: r.device_id,
        loc: r.location ?? null,
        t: r.timestamp ?? null,
        bat: r.battery ?? null,
        acc: r.accuracy ?? null,
        addr: r.address ?? null,
        fix: r.fix ?? null,
      })),
  )}|${expandKey}|${exceptionKey}`;
}

/** All track rows for a shipment; multi-device shipments (ENG-2580) return one row per device. */
function trackRowsForShipment(sid: number): ShipmentsTrackBatchRow[] {
  return (tracksQuery.data.value ?? []).filter((r) => r.shipment_id === sid);
}

/**
 * Temporal fork/merge GeoJSON for one shipment (1…N devices, same pipeline).
 * Never mixes devices across shipments.
 */
function convoyTrackPaintForShipment(sid: number, status: string | undefined) {
  const decodedRows = trackRowsForShipment(sid).map((row) => ({
    device_id: row.device_id,
    decoded: decodeShipmentTrackRow(row),
  }));
  return buildShipmentTrackDisplay(decodedRows, status).lineCollection;
}

function exceptionMapMarkers() {
  return shipmentExceptionsToMapMarkers(props.exceptions ?? [], {
    includeClosed:
      operationalMapLayersStore.layerVisibility.closedExceptions,
    devices: (props.shipments ?? []).flatMap((s) => namedTrackersFromShipment(s)),
  });
}

function collectBoundsForFit(): LatLngBounds | null {
  const boundsList: NonNullable<
    ReturnType<typeof latLngBoundsFromLngLatCoordinates>
  >[] = [];

  const lv = operationalMapLayersStore.layerVisibility;

  for (const sid of batchShipmentIds.value) {
    if (lv.track) {
      const trackFc = convoyTrackPaintForShipment(sid, undefined);
      for (const f of trackFc.features) {
        if (f.geometry.type !== 'LineString') continue;
        const c = f.geometry.coordinates as [number, number][];
        if (c.length < 2) continue;
        const b = latLngBoundsFromLngLatCoordinates(c);
        if (b) boundsList.push(b);
      }
    }

    if (lv.routeLines) {
      const routeFc = shipmentRouteSegmentsToFeatureCollection(
        routesQuery.data.value ?? [],
        sid,
        normalizedRouteQualityPercent.value,
      );
      for (const f of routeFc.features) {
        if (f.geometry.type !== 'LineString') continue;
        const c = f.geometry.coordinates as [number, number][];
        const b = latLngBoundsFromLngLatCoordinates(c);
        if (b) boundsList.push(b);
      }
    }

    const cb = lastCorridorBoundsBySid.value.get(sid);
    if (cb) {
      boundsList.push(cb);
    }

    const stops = props.shipments.find((s) => s.id === sid)?.stops ?? [];
    const stopMarkers = v2ShipmentStopsToNumberedMarkers(stops);
    for (const m of stopMarkers) {
      const { lat, lng } = m.position;
      boundsList.push({
        southWest: { lat, lng },
        northEast: { lat, lng },
      });
    }
    const stopBounds = latLngBoundsFromStopsGeojson(stops);
    if (stopBounds) boundsList.push(stopBounds);
  }

  if (lv.currentPosition) {
    const positionMarkers = convoyMarkers.value;
    for (const m of positionMarkers) {
      const { lat, lng } = m.position;
      boundsList.push({
        southWest: { lat, lng },
        northEast: { lat, lng },
      });
    }
  }

  if (lv.exceptions) {
    for (const m of exceptionMapMarkers()) {
      const { lat, lng } = m.position;
      boundsList.push({
        southWest: { lat, lng },
        northEast: { lat, lng },
      });
    }
  }

  return unionManyLatLngBounds(boundsList);
}

const allContentBounds = computed(() => collectBoundsForFit());

function collectCurrentPositionsBounds(): LatLngBounds | null {
  if (!operationalMapLayersStore.layerVisibility.currentPosition) {
    return null;
  }
  const markers = convoyMarkers.value;
  if (!markers.length) return null;
  const coords: [number, number][] = markers.map((m) => [
    m.position.lng,
    m.position.lat,
  ]);
  return latLngBoundsFromLngLatCoordinates(coords);
}

const currentPositionsBounds = computed(() => collectCurrentPositionsBounds());

function applyOperationalLayerVisibility() {
  if (!mapReady.value) return;

  const v = operationalMapLayersStore.layerVisibility;

  const pos = mapProvider.getLayer(layerPosCombinedId());
  const spiderfy = mapProvider.getLayer(layerSpiderfyCombinedId());
  if (v.currentPosition) {
    pos?.show();
    spiderfy?.show();
  } else {
    pos?.hide();
    spiderfy?.hide();
  }

  const corridorCombined = mapProvider.getLayer(layerCorridorCombinedId());
  if (v.corridor) {
    corridorCombined?.show();
  } else {
    corridorCombined?.hide();
  }

  const pathCombined = mapProvider.getLayer(layerPathCombinedId());
  const pathVerticesCombined = mapProvider.getLayer(layerPathVerticesCombinedId());
  if (v.track) {
    pathCombined?.show();
    pathVerticesCombined?.show();
  } else {
    pathCombined?.hide();
    pathVerticesCombined?.hide();
  }

  const rawPathCombined = mapProvider.getLayer(layerRawPathCombinedId());
  const rawPathVerticesCombined = mapProvider.getLayer(
    layerRawPathVerticesCombinedId(),
  );
  if (v.rawDeviceTracks) {
    rawPathCombined?.show();
    rawPathVerticesCombined?.show();
  } else {
    rawPathCombined?.hide();
    rawPathVerticesCombined?.hide();
  }

  const routeCombined = mapProvider.getLayer(layerRouteCombinedId());
  if (v.routeLines) {
    routeCombined?.show();
  } else {
    routeCombined?.hide();
  }

  const stopGeoCombined = mapProvider.getLayer(layerStopGeojsonCombinedId());
  if (v.stopGeojson) {
    stopGeoCombined?.show();
  } else {
    stopGeoCombined?.hide();
  }

  const stopsCombined = mapProvider.getLayer(layerStopsCombinedId());
  if (v.stopMarkers) {
    stopsCombined?.show();
  } else {
    stopsCombined?.hide();
  }

  const exceptionsCombined = mapProvider.getLayer(layerExceptionsCombinedId());
  if (v.exceptions) {
    exceptionsCombined?.show();
  } else {
    exceptionsCombined?.hide();
  }
}

function paintPathAndRouteLayersCombined(batchIds: number[]): void {
  if (batchIds.length === 0) {
    return;
  }

  const routes = routesQuery.data.value ?? [];
  const qp = normalizedRouteQualityPercent.value;
  const lv = operationalMapLayersStore.layerVisibility;
  const sorted = batchIds
    .slice()
    .sort((a, b) => a - b)
    .join(',');
  const layerKey = `${lv.track ? '1' : '0'}${lv.routeLines ? '1' : '0'}`;
  const combinedPaintKey = `${sorted}|${layerKey}`;

  type RowMeta = {
    fp: string;
    status: string | undefined;
  };

  const metaBySid = new Map<number, RowMeta>();
  for (const sid of batchIds) {
    const trackRows = trackRowsForShipment(sid);
    const status = props.shipments.find((s) => s.id === sid)?.status;
    const baseFp = trackRows.length
      ? trackRows
          .map((row) =>
            fingerprintPathAndRouteLineLayers(sid, row, routes, status, qp),
          )
          .join('|')
      : fingerprintPathAndRouteLineLayers(sid, null, routes, status, qp);
    // Invalidate paint when temporal graph semantics change (fork/merge options).
    const fp = `${baseFp}|tg:${CONVOY_TRACK_GRAPH_OPTIONS_VERSION}`;
    metaBySid.set(sid, { fp, status });
  }

  const fpMatch =
    lastPathRouteCombinedPaintKey.value === combinedPaintKey &&
    batchIds.every(
      (sid) => metaBySid.get(sid)!.fp === pathRouteFingerprintBySid.value[sid],
    );

  // Same track/route payload (e.g. background refetch) → skip Leaflet setGeoJSON.
  if (fpMatch) {
    return;
  }

  const pathFeatures: GeoJsonFeatureCollection['features'] = [];
  const routeFeatures: GeoJsonFeatureCollection['features'] = [];

  const pathLayer = mapProvider.addLayer(layerPathCombinedId());
  const routeLayer = mapProvider.addLayer(layerRouteCombinedId());

  for (const sid of batchIds) {
    const { fp, status } = metaBySid.get(sid)!;
    if (lv.track) {
      // Per-shipment temporal fork/merge (1…N devices, same pipeline; no cross-shipment merge).
      const trackFc = convoyTrackPaintForShipment(sid, status);
      pathFeatures.push(...trackFc.features);
    }
    if (lv.routeLines) {
      const routeFc = shipmentRouteSegmentsToFeatureCollection(routes, sid, qp);
      routeFeatures.push(...routeFc.features);
    }
    pathRouteFingerprintBySid.value[sid] = fp;
  }

  if (pathFeatures.length) {
    pathLayer.setGeoJSON({ type: 'FeatureCollection', features: pathFeatures });
  } else {
    pathLayer.clear();
  }
  if (routeFeatures.length) {
    routeLayer.setGeoJSON({
      type: 'FeatureCollection',
      features: routeFeatures,
    });
  } else {
    routeLayer.clear();
  }
  lastPathRouteCombinedPaintKey.value = combinedPaintKey;
}

/**
 * One Leaflet GeoJSON layer for all shipment corridors: a single setGeoJSON avoids
 * N full map redraws when many shipments are on screen.
 */
async function paintCorridorLayersCombined(batchIds: number[]): Promise<void> {
  const combinedId = layerCorridorCombinedId();

  if (!operationalMapLayersStore.layerVisibility.corridor) {
    mapProvider.getLayer(combinedId)?.clear();
    lastCorridorCombinedPaintKey.value = '';
    for (const sid of batchIds) {
      lastCorridorBoundsBySid.value.delete(sid);
      delete corridorInputHashBySid.value[sid];
    }
    return;
  }

  if (batchIds.length === 0) {
    mapProvider.getLayer(combinedId)?.clear();
    lastCorridorCombinedPaintKey.value = '';
    return;
  }

  const routes = routesQuery.data.value ?? [];
  const qp = normalizedRouteQualityPercent.value;
  const alg = CORRIDOR_BUFFER_ALGORITHM_VERSION;
  const sortedIdsKey = batchIds
    .slice()
    .sort((a, b) => a - b)
    .join(',');

  const hashEntries = await Promise.all(
    batchIds.map(async (sid) => {
      const h = await computeShipmentCorridorInputHash(routes, sid, alg, qp);
      return [sid, h] as const;
    }),
  );
  const hashes = Object.fromEntries(hashEntries) as Record<number, string>;

  const hashFingerprintsMatch = batchIds.every(
    (sid) => hashes[sid] === corridorInputHashBySid.value[sid],
  );
  if (
    hashFingerprintsMatch &&
    lastCorridorCombinedPaintKey.value === sortedIdsKey
  ) {
    return;
  }

  const fcs = await Promise.all(
    batchIds.map((sid) =>
      shipmentHasCorridorHashableRoutes(routes, sid)
        ? buildShipmentRouteCorridorFeatureCollection(routes, sid, {
            qualityPercent: qp,
          })
        : Promise.resolve({ type: 'FeatureCollection' as const, features: [] }),
    ),
  );

  const mergedFeatures: GeoJsonFeatureCollection['features'] = [];
  const corridorLayer = mapProvider.addLayer(combinedId);

  try {
    for (let i = 0; i < batchIds.length; i++) {
      const sid = batchIds[i]!;
      const corridorFc = fcs[i]!;
      const b = latLngBoundsFromPolygonFeatureCollection(corridorFc);
      if (b) {
        lastCorridorBoundsBySid.value.set(sid, b);
      } else {
        lastCorridorBoundsBySid.value.delete(sid);
      }
      mergedFeatures.push(...corridorFc.features);
      corridorInputHashBySid.value[sid] = hashes[sid]!;
    }

    if (mergedFeatures.length) {
      corridorLayer.setGeoJSON({
        type: 'FeatureCollection',
        features: mergedFeatures,
      });
    } else {
      corridorLayer.clear();
    }
    lastCorridorCombinedPaintKey.value = sortedIdsKey;
  } catch {
    corridorLayer.clear();
    lastCorridorCombinedPaintKey.value = '';
    for (const sid of batchIds) {
      lastCorridorBoundsBySid.value.delete(sid);
      delete corridorInputHashBySid.value[sid];
    }
  }
}

function paintPositionsAndStopsLayers() {
  const posLayer = mapProvider.addLayer(layerPosCombinedId());

  const posFp = fingerprintOperationalPositionsPayload(
    positionsQuery.data.value ?? [],
  );
  if (lastPositionsPaintFingerprint.value !== posFp) {
    // Per-shipment proximity clusters (never merge across shipments).
    const live = livePaint.value;
    if (live.markers.length) {
      posLayer.setMarkers(live.markers);
    } else {
      posLayer.clear();
    }
    const spiderfyLayer = mapProvider.addLayer(layerSpiderfyCombinedId());
    if (live.spiderfyLegs.features.length) {
      spiderfyLayer.setGeoJSON(live.spiderfyLegs);
    } else {
      spiderfyLayer.clear();
    }
    lastPositionsPaintFingerprint.value = posFp;
    reapplyMapInteractions();
  }

  const stopGeoLayer = mapProvider.addLayer(layerStopGeojsonCombinedId());
  const stopsMarkersLayer = mapProvider.addLayer(layerStopsCombinedId());

  const mergedStopFeatures: GeoJsonFeatureCollection['features'] = [];
  const mergedStopMarkers: MapMarker[] = [];

  for (const row of props.shipments) {
    const sid = row.id;
    const stops = row.stops ?? [];
    const boundariesFc = v2ShipmentStopsToBoundariesFeatureCollection(stops);
    if (boundariesFc?.features.length) {
      mergedStopFeatures.push(...boundariesFc.features);
    }
    const stopPins = v2ShipmentStopsToNumberedMarkers(stops);
    for (const m of stopPins) {
      mergedStopMarkers.push({ ...m, id: `s${sid}:${m.id}` });
    }
  }

  if (mergedStopFeatures.length) {
    stopGeoLayer.setGeoJSON({
      type: 'FeatureCollection',
      features: mergedStopFeatures,
    });
  } else {
    stopGeoLayer.clear();
  }
  if (mergedStopMarkers.length) {
    stopsMarkersLayer.setMarkers(mergedStopMarkers);
  } else {
    stopsMarkersLayer.clear();
  }
}

function paintExceptionsLayer() {
  const layer = mapProvider.addLayer(layerExceptionsCombinedId());
  const markers = exceptionMapMarkers();
  if (markers.length) {
    layer.setMarkers(markers);
  } else {
    layer.clear();
  }
}

function clearOperationalShipmentLayers(sids: number[]) {
  mapProvider.getLayer(layerPosCombinedId())?.clear();
  mapProvider.getLayer(layerSpiderfyCombinedId())?.clear();
  mapProvider.getLayer(layerCorridorCombinedId())?.clear();
  mapProvider.getLayer(layerPathCombinedId())?.clear();
  mapProvider.getLayer(layerPathVerticesCombinedId())?.clear();
  clearRawDeviceTracksLayers();
  mapProvider.getLayer(layerRouteCombinedId())?.clear();
  mapProvider.getLayer(layerStopGeojsonCombinedId())?.clear();
  mapProvider.getLayer(layerStopsCombinedId())?.clear();
  mapProvider.getLayer(layerExceptionsCombinedId())?.clear();
  lastCorridorCombinedPaintKey.value = '';
  lastPathRouteCombinedPaintKey.value = '';
  lastTrackVerticesPaintKey.value = '';
  fullTrackVertices.value = [];
  lastPositionsPaintFingerprint.value = '';
  for (const sid of sids) {
    delete pathRouteFingerprintBySid.value[sid];
    delete corridorInputHashBySid.value[sid];
    lastCorridorBoundsBySid.value.delete(sid);
  }
}

function clearCorridorLayersFor(sids: number[]) {
  mapProvider.getLayer(layerCorridorCombinedId())?.clear();
  lastCorridorCombinedPaintKey.value = '';
  for (const sid of sids) {
    lastCorridorBoundsBySid.value.delete(sid);
    delete corridorInputHashBySid.value[sid];
  }
}

function schedulePaintOperationalLayers() {
  const ids = batchShipmentIds.value;
  if (!mapReady.value) return;
  if (!operationalLayersPaintEnabled.value) return;

  if (!ids.length) {
    clearOperationalShipmentLayers(lastPaintedShipmentIds.value);
    lastPaintedShipmentIds.value = [];
    lastCorridorBoundsBySid.value.clear();
    userAdjustedMapView.value = false;
    mapMoveIntentTrackingEnabled.value = false;
    applyOperationalLayerVisibility();
    return;
  }

  void (async () => {
    const prev = lastPaintedShipmentIds.value;
    const dropped = prev.filter((id) => !ids.includes(id));
    if (dropped.length) {
      clearOperationalShipmentLayers(dropped);
      clearCorridorLayersFor(dropped);
    }

    paintPathAndRouteLayersCombined(ids);
    paintTrackVerticesCombined(ids);
    paintRawDeviceTracksCombined(ids);
    requestAnimationFrame(() => {
      const corridorPaintPromise = operationalMapLayersStore.layerVisibility
        .corridor
        ? paintCorridorLayersCombined(ids)
        : (clearCorridorLayersFor(ids), Promise.resolve());

      void corridorPaintPromise.then(() => {
        requestAnimationFrame(() => {
          paintPositionsAndStopsLayers();
          paintExceptionsLayer();
          applyOperationalLayerVisibility();
          lastPaintedShipmentIds.value = [...ids];
          void nextTick(() => {
            const merged = collectBoundsForFit();
            if (merged) {
              tryAutoFitToContentBounds(merged);
            } else if (!mapBusy.value) {
              mapMoveIntentTrackingEnabled.value = true;
            }
          });
        });
      });
    });
  })();
}

watch(
  () => [
    mapReady.value,
    batchShipmentIds.value.join(','),
    tracksQuery.data.value,
    routesQuery.data.value,
    positionsQuery.data.value,
    props.shipments,
    normalizedRouteQualityPercent.value,
    operationalMapLayersStore.layerVisibility.track,
    operationalMapLayersStore.layerVisibility.rawDeviceTracks,
    operationalMapLayersStore.layerVisibility.routeLines,
    operationalMapLayersStore.layerVisibility.corridor,
    operationalMapLayersStore.layerVisibility.currentPosition,
    operationalMapLayersStore.layerVisibility.closedExceptions,
    [...expandedShipmentIds.value].join(','),
    props.exceptions,
  ],
  () => {
    schedulePaintOperationalLayers();
  },
  { deep: true },
);

watch(
  () => operationalMapLayersStore.layerVisibility,
  () => {
    applyOperationalLayerVisibility();
  },
  { deep: true },
);

watch(
  () => operationalMapLayersStore.layerVisibility.corridor,
  (enabled, wasEnabled) => {
    if (!mapReady.value || enabled === wasEnabled) return;
    if (enabled) {
      schedulePaintOperationalLayers();
    } else {
      clearCorridorLayersFor(batchShipmentIds.value);
    }
  },
);

function tryAutoFitToContentBounds(bounds: LatLngBounds): void {
  if (userAdjustedMapView.value) {
    return;
  }
  treatNextMoveEndAsProgrammatic.value = true;
  mapProvider.fitBounds(bounds);
  window.setTimeout(() => {
    if (treatNextMoveEndAsProgrammatic.value) {
      treatNextMoveEndAsProgrammatic.value = false;
    }
    mapMoveIntentTrackingEnabled.value = true;
  }, 500);
}

function onMapReady() {
  offMapMoveEnd = mapProvider.onMoveEnd(() => {
    if (treatNextMoveEndAsProgrammatic.value) {
      treatNextMoveEndAsProgrammatic.value = false;
      mapMoveIntentTrackingEnabled.value = true;
      return;
    }
    if (!mapMoveIntentTrackingEnabled.value) {
      return;
    }
    userAdjustedMapView.value = true;
  });
  mapReady.value = true;
  operationalLayersPaintEnabled.value = false;
  void afterNextBrowserPaint().then(() => {
    operationalLayersPaintEnabled.value = true;
    schedulePaintOperationalLayers();
  });
}
</script>

<template>
  <div
    class="relative flex size-full min-h-0 flex-1 flex-col"
    :aria-busy="mapBusy ? 'true' : 'false'"
  >
    <div
      v-if="mapBusy"
      class="pointer-events-auto absolute inset-0 z-[500] flex cursor-wait items-center justify-center rounded-md bg-white/80 backdrop-blur-[1px]"
      role="status"
      aria-label="Loading map"
    >
      <ProgressSpinner
        :stroke-width="PVT_SPINNER_STROKE_WIDTH"
        :style="PVT_SPINNER_STYLE_OVERLAY"
        aria-hidden="true"
      />
    </div>
    <div class="relative min-h-0 flex-1">
      <GpxMap
        class="size-full min-h-0"
        :map-provider="mapProvider"
        @ready="onMapReady"
      >
        <template #top-right>
          <GpxMapBasemapToggle v-model="basemapMode" />
        </template>
        <template #bottom-left>
          <ShipmentOperationalMapLayersMenu
            :options="[...OPERATIONAL_MAP_LAYER_OPTIONS]"
          />
        </template>
        <template #right-center>
          <div class="flex flex-col items-end gap-1">
            <GpxMapZoomControl :max-zoom="20" />
            <GpxMapFitBoundsControl
              v-if="allContentBounds"
              :bounds="allContentBounds"
              button-title="Fit all map content"
              aria-label="Fit all map content"
            />
            <GpxMapFitBoundsControl
              v-if="currentPositionsBounds"
              :bounds="currentPositionsBounds"
              button-title="Fit to current device positions"
              aria-label="Fit to current device positions"
            >
              <CircleFill />
            </GpxMapFitBoundsControl>
          </div>
        </template>
      </GpxMap>
      <ConvoyGroupHoverOverlay
        :trackers="groupHoverTrackers"
        :screen-point="screenPoint"
      />
    </div>
  </div>
</template>
